// Phase 6.5 — operator-readable GNSS-system labels for the Feed
// surface (BatchCard error breakdown, per-config rows).
//
// Mirror of the Python helper in ``Code/gnss_labels.py`` — both
// halves use the same abbreviations (Russian short forms for
// GLO / GAL / BDS, English for GPS) and the same «все 4» collapse.
//
// Distinct from ``components/analytics/formatConfig.ts`` (which
// renders canonical comma-joined keys for the analytics screen
// with the longer "GLONASS" / "Galileo" / "BeiDou" labels). The
// Feed surface has tighter line-width budgets — Russian short
// forms read more compactly inside a card.

const GNSS_DISPLAY: Record<string, string> = {
  gps: 'GPS',
  glo: 'ГЛО',
  glonass: 'ГЛО',
  gal: 'GAL',
  galileo: 'GAL',
  bds: 'BDS',
  beidou: 'BDS',
};

// Same canonical "all four" set as the backend helper. Order
// inside this set is irrelevant — the check below is by membership.
const ALL_FOUR_SYSTEMS: ReadonlySet<string> = new Set([
  'gps',
  'glonass',
  'galileo',
  'beidou',
]);

// Stable display order — GPS first, then ГЛО / GAL / BDS, then any
// unknown future code alphabetically. Matches the backend ordering
// so a string like "GPS, ГЛО, GAL, BDS" is produced identically on
// both sides.
const DISPLAY_ORDER = ['gps', 'glo', 'glonass', 'gal', 'galileo', 'bds', 'beidou'];

const EMPTY_PLACEHOLDER = '—';

export interface FormatGnssListOptions {
  /** When `false`, a 4-system set renders as "GPS, ГЛО, GAL, BDS"
   *  instead of "все 4". Useful when the visual context already
   *  implies «все 4» and the explicit comma-list reads better. */
  collapseAllFour?: boolean;
}

/** Render a list of GNSS-system codes as a human-readable phrase.
 *
 *  Examples:
 *  ```
 *  formatGnssList(['gps'])                                  // 'GPS'
 *  formatGnssList(['gps', 'glonass'])                       // 'GPS, ГЛО'
 *  formatGnssList(['beidou', 'galileo', 'glonass', 'gps'])  // 'все 4'
 *  formatGnssList([], { collapseAllFour: false })           // '—'
 *  formatGnssList(null)                                     // '—'
 *  ```
 *
 *  Behaviour mirrors the Python helper in ``Code/gnss_labels.py``:
 *  duplicates are collapsed (set semantics), the GLO/GLONASS-style
 *  aliases share a single label, unknown codes pass through
 *  upper-cased so a future ``"qzss"`` renders as `"QZSS"` instead
 *  of being silently dropped.
 */
export function formatGnssList(
  systems: ReadonlyArray<string> | null | undefined,
  options: FormatGnssListOptions = {},
): string {
  const { collapseAllFour = true } = options;
  if (!systems || systems.length === 0) return EMPTY_PLACEHOLDER;

  const normalised = new Set(systems.map((s) => s.toLowerCase()));

  if (collapseAllFour) {
    let coverage = 0;
    for (const code of ALL_FOUR_SYSTEMS) {
      if (normalised.has(code)) coverage += 1;
    }
    if (coverage === ALL_FOUR_SYSTEMS.size) return 'все 4';
  }

  const seenLabels: string[] = [];
  const seenSet = new Set<string>();
  for (const code of DISPLAY_ORDER) {
    if (!normalised.has(code)) continue;
    // `noUncheckedIndexedAccess` — explicit guard so TS knows the
    // lookup is safe (every key in DISPLAY_ORDER is also in
    // GNSS_DISPLAY by construction, but the type system can't infer
    // that from the dict shape).
    const label = GNSS_DISPLAY[code];
    if (label === undefined) continue;
    if (!seenSet.has(label)) {
      seenSet.add(label);
      seenLabels.push(label);
    }
  }
  // Append unknown codes alphabetically so we don't silently drop them.
  for (const code of Array.from(normalised).sort()) {
    if (code in GNSS_DISPLAY) continue;
    const upper = code.toUpperCase();
    if (!seenSet.has(upper)) {
      seenSet.add(upper);
      seenLabels.push(upper);
    }
  }
  return seenLabels.join(', ');
}
